import Rectangle from "src/dataTypes/geometry/Rectangle";
import Point from "src/dataTypes/geometry/Point";
import NumberList from "src/dataTypes/numeric/NumberList";
import NumberTable from "src/dataTypes/numeric/NumberTable";
import NumberListGenerators from "src/operators/numeric/numberList/NumberListGenerators";
import ListGenerators from "src/operators/lists/ListGenerators";
import TableGenerators from "src/operators/lists/TableGenerators";
import NumberOperators from "src/operators/numeric/NumberOperators";
import Interval from "src/dataTypes/numeric/Interval";
import { instantiateWithSameType, typeOf, instantiate } from "src/tools/utils/code/ClassUtils";

/**
 * @classdesc NumberList Operators
 *
 * @namespace
 * @category numbers
 */
function NumberListOperators() {}
export default NumberListOperators;

/**
 * Returns dot product between two numberLists
 * @param  {NumberList1} numberList1 NumberList of the same length as numberList2.
 * @param  {NumberList2} numberList2 NumberList of the same length as numberList1.
 * @return {Number} Dot product between two lists.
 * tags:statistics
 */
NumberListOperators.dotProduct = function(numberList1, numberList2) {
  if(numberList1 == null || numberList2 == null) return null;
  var sum = 0;
  var i;
  var nElements = Math.min(numberList1.length, numberList2.length);
  for(i = 0; i < nElements; i++) {
    sum += numberList1[i] * numberList2[i];
  }
  return sum;
};

/**
 * Returns linear regression between two numberLists in another numberList with items
 * slope, intercept, r squared, n OR in a Point representing the line
 * @param  {NumberList} numberListX of the same length as numberListY
 *
 * @param  {NumberList} numberListY nothing, or numberListY of the same length as numberListX
 * @param  {Number} returnType <br>0:NumberList with items slope, intercept, r squared, n (default)<br>1: Point with slope,intercept
 * @return {Object} result depending on returnType
 * tags:statistics
 */
NumberListOperators.linearRegression = function(numberListX, numberListY, returnType) {
  returnType = returnType == null || returnType > 1 ? 0:returnType;
  var numberListR = new NumberList();

  if(numberListX == null ||
     (numberListY != null && numberListX.length != numberListY.length) ||
     numberListX.length === 0)
    return returnType===0?numberListR:new Point();

  if(numberListY==null){
    numberListY = numberListX;
    numberListX = NumberListGenerators.createSortedNumberList(numberListX.length, 0, 1);
  }

  var sumx=0,sumy=0,sumx2=0,sumxy=0,sumy2=0;

  var n = numberListX.length;
  for(var i = 0; i < n; i++) {
    sumx += numberListX[i];
    sumy += numberListY[i];
    sumx2 += numberListX[i]*numberListX[i];
    sumxy += numberListX[i]*numberListY[i];
    sumy2 += numberListY[i]*numberListY[i];
  }
  var denominator = n*sumx2 - sumx*sumx;
  var slope = denominator===0?0 : (n * sumxy - sumx * sumy) / denominator;

  var intercept = (sumy / n) - (slope * sumx) / n;
  if(returnType==1)
    return new Point(slope,intercept);
  var r2 = Math.pow((n*sumxy - sumx*sumy)/Math.sqrt((n*sumx2-sumx*sumx)*(n*sumy2-sumy*sumy)),2);
  numberListR.push(slope);
  numberListR.push(intercept);
  numberListR.push(r2);
  numberListR.push(n);
  return numberListR;
};

/**
 * Calculates Euclidean distance between two numberLists
 * @param  {NumberList} numberList NumberList of the same length as numberList2.
 * @param  {NumberList} numberList NumberList of the same length as numberList1.
 *
 * @param  {String} distanceMetric is the type of distance metric to return. Values:<br>euclidean (default)<br>cosine<br>manhattan
 * @return {Number} Summed Euclidean distance between all values.
 * tags:distance,compare
 */
NumberListOperators.distance = function(numberList1, numberList2, distanceMetric) {
  if(numberList1==null || numberList2==null) return;
  var sum = 0;
  var i;
  var nElements = Math.min(numberList1.length, numberList2.length);
  distanceMetric = distanceMetric == null ? 'euclidean' : distanceMetric;
  distanceMetric = distanceMetric.toLowerCase();
  if(distanceMetric == 'cosine')
    return (1 - NumberListOperators.cosineSimilarity(numberList1,numberList2));
  if(distanceMetric == 'manhattan'){
    for(i = 0; i < nElements; i++) {
      sum += Math.abs(numberList1[i] - numberList2[i]);
    }
    return sum;
  }
  if(distanceMetric != 'euclidean')
    throw new Error('Invalid distanceMetric.');
  // euclidean
  for(i = 0; i < nElements; i++) {
    sum += Math.pow(numberList1[i] - numberList2[i], 2);
  }
  return Math.sqrt(sum);
};

/**
 * cosine similarity, used to compare two NumberLists regardless of norm (see: http://en.wikipedia.org/wiki/Cosine_similarity)
 * @param  {NumberList} numberList0
 * @param  {NumberList} numberList1
 *
 * @param  {Number} norm0 accelerates operations if the norm of the first numberList has benn previously calculated and provided
 * @param  {Number} norm1 accelerates operations if the norm of the second numbeList has benn previously calculated and provided
 * @return {Number}
 * tags:distance,compare
 */
NumberListOperators.cosineSimilarity = function(numberList0, numberList1, norm0, norm1) {
  if(numberList0 == null || numberList1 == null) return null;
  norm0 = norm0==null?numberList0.getNorm():norm0;
  norm1 = norm1==null?numberList1.getNorm():norm1;
  var norms = norm0 * norm1;
  if(norms === 0) return 0;
  return NumberListOperators.dotProduct(numberList0, numberList1) / norms;
};

/**
 * calculates the covariance between two numberLists
 * @param  {NumberList} numberList0
 * @param  {NumberList} numberList1
 *
 * @param {Boolean} bUseInfoObject if true use the infoObject values (default: false)
 * @return {Number}
 * tags:statistics
 */
NumberListOperators.covariance = function(numberList0, numberList1,bUseInfoObject) {
  if(numberList0==null || numberList1==null) return;

  var l = Math.min(numberList0.length, numberList1.length);
  var i,av0,av1,s=0;
  if(bUseInfoObject && numberList0.infoObject)
    av0 = numberList0.infoObject.average;
  else
    av0 = numberList0.getAverage();
  if(bUseInfoObject && numberList1.infoObject)
    av1 = numberList1.infoObject.average;
  else
    av1 = numberList1.getAverage();

  for(i = 0; i<l; i++) {
    s += (numberList0[i] - av0)*(numberList1[i] - av1);
  }

  return s/l;
};


/**
 * @todo finish docs, maybe change name
 */
NumberListOperators.standardDeviationBetweenTwoNumberLists = function(numberList0, numberList1) {
  if(numberList0==null || numberList1==null) return;
  var s = 0;
  var l = Math.min(numberList0.length, numberList1.length);
  var i;

  for(i = 0; i < l; i++) {
    s += Math.pow(numberList0[i] - numberList1[i], 2);
  }

  return s/l;
};

/**
 * returns Pearson Product Moment Correlation, the most common correlation coefficient ( covariance/(standard_deviation0*standard_deviation1) )
 * @param  {NumberList} numberList0
 * @param  {NumberList} numberList1
 *
 * @param {Number} sd0 standard deviation of list 0, accelerates opeartion if previously calculated
 * @param {Number} sd1 standard deviation of list 1, accelerates opeartion if previously calculated
 * @param {Boolean} bUseInfoObject if true use the infoObject values (default: false)
 * @return {Number}
 * tags:statistics
 */
NumberListOperators.pearsonProductMomentCorrelation = function(numberList0, numberList1, sd0, sd1, bUseInfoObject) {
  if(numberList0==null || numberList1==null) return;
  if(sd0==null && bUseInfoObject && numberList0.infoObject)
    sd0 = numberList0.infoObject.standardDeviation;
  if(sd1==null && bUseInfoObject && numberList1.infoObject)
    sd1 = numberList1.infoObject.standardDeviation;
  if(sd0==null) sd0 = numberList0.getStandardDeviation();
  if(sd1==null) sd1 = numberList1.getStandardDeviation();
  
  var stndDeviations = sd0*sd1;//numberList0.getStandardDeviation() * numberList1.getStandardDeviation();
  if(stndDeviations===0) return 0;
  return NumberListOperators.covariance(numberList0, numberList1,bUseInfoObject) / stndDeviations;
};

/**
 * returns a NumberList giving correlation around each point considering n points on either side
 * @param  {NumberList} numberList0
 * @param  {NumberList} numberList1
 *
 * @param {Number} nPoints is half the width of the window of points to consider (default:5)
 * @return {NumberList}
 * tags:statistics
 */
NumberListOperators.slidingWindowCorrelation = function(numberList0, numberList1, nPoints) {
  if(numberList0==null || numberList1==null || numberList0.length != numberList1.length) return;
  if(nPoints == null || isNaN(nPoints) || nPoints < 1)
    nPoints = 5;
  nPoints = Math.round(nPoints);

  var i,j,nLt0,nLt1,corr;
  var len = numberList0.length;
  var nLResult = new NumberList();
  for(i=0;i<len;i++){
    nLt0 = new NumberList();
    nLt1 = new NumberList();
    for(j=Math.max(0,i-nPoints); j <= i+nPoints; j++){
      if(j<0 || j >= len) continue;
      nLt0.push(numberList0[j]);
      nLt1.push(numberList1[j]);
    }
    corr = NumberListOperators.pearsonProductMomentCorrelation(nLt0,nLt1);
    nLResult.push(corr);
  }
  return nLResult;
};

/**
 * returns a NumberList normalized to the sum, all its values will add up 1 (or optionally provided factor)
 * @param  {NumberList} numberlist NumberList to Normalize.
 *
 * @param {Number} factor optional factor value (values will add up factor)
 * @param {Number} sum provide this value if sum has been previously calculated, it will reduce calculations. If not provided, sum will be calculated automatically.
 * @return {NumberList} new NumberList of values normalized to the sum
 * tags:
 */
NumberListOperators.normalizeToSum = function(numberlist, factor, sum) {
  if(numberlist==null) return;
  
  factor = factor == null ? 1 : factor;
  var newNumberList = new NumberList();
  newNumberList.name = numberlist.name;
  if(numberlist.length === 0) return newNumberList;
  var i;
  sum = sum == null ? numberlist.getSum() : sum;
  if(sum === 0) return numberlist.clone();

  factor/=sum;

  for(i = 0; i < numberlist.length; i++) {
    //newNumberList.push(factor * numberlist[i] / sum);
    newNumberList[i] = factor*numberlist[i];
  }
  return newNumberList;
};

/**
 * @param  {NumberList}
 *
 * @param {Number}
 * @param {Number}
 * @return {NumberList}
 * tags:deprecated
 * replaceBy:normalizeToSum
 */
NumberListOperators.normalizedToSum = function(numberlist, factor, sum) {
  return NumberListOperators.normalizeToSum(numberlist, factor, sum);
};


/**
 * Returns a NumberList normalized to min-max interval.
 *
 * @param  {NumberList} numberlist NumberList to Normalize.
 * @param {Number} factor Optional multiplier to modify the normalized values by.
 * Defaults to 1.
 * @return {NumberList}
 * tags:deprecated
 */
NumberListOperators.normalized = function(numberlist, factor) {//@todo: remove
  if(numberlist==null) return;
  return numberlist.getNormalized();
};

/**
 * Returns a NumberList normalized to z-scores (mean of 0, stdev of 1).
 * @param  {NumberList} numberlist NumberList to Normalize.
 * @return {NumberList}
 * tags:statistics
 */
NumberListOperators.normalizeByZScore = function(numberlist) {
  if(numberlist==null) return;
  if(numberlist.length === 0) return null;

  var i;
  var mean = numberlist.getAverage();
  var stddev = numberlist.getStandardDeviation();
  if(stddev===0) stddev=1; // all returned values will be zero

  var newNumberList = new NumberList();
  for(i = 0; i < numberlist.length; i++) {
    newNumberList.push((numberlist[i] - mean) / stddev);
  }
  newNumberList.name = numberlist.name;
  return newNumberList;
};

/**
 * Returns a NumberList normalized to Max.
 * @param  {NumberList} numberlist NumberList to Normalize.
 *
 * @param {Number} factor Optional multiplier to modify the normalized values by. Defaults to 1.
 * @return {NumberList}
 * tags:
 */
NumberListOperators.normalizeToMax = function(numberlist, factor) {
  if(numberlist==null) return;
  
  factor = factor == null ? 1 : factor;

  if(numberlist.length === 0) return null;

  var max = numberlist.getMax();
  if(max === 0) {
    max = numberlist.getMin();
    if(max === 0) return ListGenerators.createListWithSameElement(numberlist.length, 0);
  }
  var newNumberList = new NumberList();
  for(var i = 0; numberlist[i] != null; i++) {
    newNumberList.push(factor * (numberlist[i] / max));
  }
  newNumberList.name = numberlist.name;
  return newNumberList;
};


/**
 * Returns a NumberList normalized to an Interval.
 * @param  {NumberList} numberlist NumberList to Normalize.
 * @param {Interval} interval that defines the range of the new numberList
 * @return {NumberList}
 * tags:
 */
NumberListOperators.normalizeToInterval = function(numberlist, interval) {
  if(numberlist==null || interval==null || numberlist.length === 0) return;

  var i;
  var numberListInterval = numberlist.getInterval();
  var nLAmplitude = numberListInterval.getAmplitude();
  if(nLAmplitude===0) return ListGenerators.createListWithSameElement (numberlist.length, interval.x, numberlist.name);
  var amplitude = interval.getSignedAmplitude();
  var factor = amplitude/nLAmplitude;
  var newNumberList = new NumberList();
  for(i = 0; i < numberlist.length; i++) {
    newNumberList.push( interval.x + factor*(numberlist[i] - numberListInterval.x) );
  }
  newNumberList.name = numberlist.name;
  return newNumberList;
};

/**
 * Returns a NumberList trimed belwo and above, replacing values under parameter min and above parameter max
 * @param  {NumberList} numberlist to trim
 *
 * @param {Number} min to replace any value below (if null no bottom replacements are made)
 * @param {Number} max to replace any value above (if null no top replacements are made)
 * @return {NumberList}
 * tags:transform
 */
NumberListOperators.trimNumberList = function(numberlist, min, max) {
  if(numberlist == null) return null;
  return numberlist.trim(min, max);
};

/**
 * Returns a numberList with accumulated values (each value in the new list being the sum of current and all previous values in the original)
 * @param  {NumberList} numberlist NumberList to Normalize.
 * @return {NumberList}
 * tags:
 */
NumberListOperators.getAccumulativeSum = function(numberlist) {
  if(numberlist==null || numberlist.length === 0) return;

  var newNumberList = new NumberList();

  newNumberList[0] = numberlist[0];

  for(var i = 1; i < numberlist.length; i++) {
    newNumberList[i] = numberlist[i] + newNumberList[i-1];
  }

  return newNumberList;
};


/**
 * generates a new numberList of desired size (smaller than original), with elements claculated as averages of neighbors
 * @param  {NumberList} numberList
 * @param  {Number} newLength length of returned numberList
 * @return {NumberList}
 * tags:statistics
 */
NumberListOperators.shorten = function(numberList, newLength) {
  if(numberList==null) return null;
  if(newLength==null || newLength>=numberList.length) return numberList;

  var windowSize = numberList.length/newLength;
  var newNumberList = new NumberList();
  var windowSizeInt = Math.floor(windowSize);
  var val;
  var i, j, j0;

  newNumberList.name = numberList.name;

  for(i=0; i<newLength; i++){
    j0 = Math.floor(i*windowSize);
    val = 0;
    for(j=0; j<windowSizeInt; j++){
      val += numberList[j0+j];
    }
    newNumberList[i] = val/windowSizeInt;
  }
  return newNumberList;
};

/**
 * simplifies a numer list, by keeping the nCategories-1 most common values, and replacing the others with an "other" element
 * this method reduces the number of different values contained in the list, converting it into a categorical list
 * @param  {NumberList} numberList NumberList to shorten
 *
 * @param  {Number} method simplification method:<b>0:significant digits (default)<br>1:quantiles (value will be min value in percentile)<br>2:orders of magnitude
 * @param  {Number} param different meaning according to choosen method:<br>0:number of significant digits<br>1:number of quantiles<br>2:no need of param
 * @return {NumberList} simplified list
 * tags:
 */
NumberListOperators.simplify = function(numberList, method, param) {
  if(numberList==null) return;

  method = method||0;
  param = param||0;

  var newList = new NumberList();
  newList.name = numberList.name;


  switch(method){
    case 0:
      var power = Math.pow(10, param);
      numberList.forEach(function(val){
        newList.push(Math.floor(val/power)*power);
      });
      break;
    case 1:
      //deploy quantiles first (optional return of n percentile, min value, interval, numberTable with indexes, numberTable with values)
      break;
  }

  return newList;
};

/**
 * based on limits that define segments, creates a numberList with the segment number for each value
 * @param  {NumberList} numberList
 * @param  {NumberList} limits that define the segments, the maximum number of different values is the number of limits plus one
 *
 * @param {Number} mode 0: (default) returns interval number<|>1: returns interval expressed as text (e.g. "1000-1500")
 * @return {NumberList} list with segment number for each value, or list with intervals expressed as text
 * tags:statistics,extract
 */
NumberListOperators.segmentNumberList = function(nL,limits,mode) {
    if(nL == null || limits == null) return null;
    mode = mode==null?0:mode;

    var ranges = mode==0?new NumberList():new mo.StringList();
    ranges.name = "segments "+nL.name;
    for(var i=0;i<nL.length;i++){
        var ranged = false;
        for(var j=0;j<limits.length;j++){
            if(nL[i]<limits[j]){
                ranged=true;
                ranges[i] = mode==0?j:( j==0?"<"+limits[j]:limits[j-1]+"-"+limits[j] );
                break;
            }
        }
        if(!ranged) ranges[i] = mode==0?limits.length:">="+limits[limits.length-1];
    }
    return ranges;
};

/**
 * calculates k-means clusters of values in a numberList
 * @param  {NumberList} numberList
 * @param  {Number} k number of clusters
 *
 * @param {Boolean} returnIndexes return clusters of indexes rather than values (false by default)
 * @return {NumberTable} numberLists each being a cluster
 * tags:ds
 */
NumberListOperators.linearKMeans = function(numberList, k, returnIndexes) {
  if(numberList == null || k == null || (k <= 0)) {
    return null;
  }

  var interval = numberList.getInterval();

  var min = interval.x;
  var max = interval.y;
  var clusters = new NumberTable();
  var i, j;
  var jK;
  var x;
  var dX = (max - min) / k;
  var d;
  var dMin;
  var n;
  var N = 1000;
  var means = new NumberList();
  var nextMeans = new NumberList();
  var nValuesInCluster = new NumberList();
  var length = numberList.length;

  var initdMin = 1 + max - min;

  for(i = 0; i < k; i++) {
    clusters[i] = new NumberList();
    nextMeans[i] = min + (i + 0.5) * dX;
  }

  for(n = 0; n < N; n++) {

    for(i = 0; i < k; i++) {
      nValuesInCluster[i] = 0;
      means[i] = nextMeans[i];
      nextMeans[i] = 0;
    }

    for(i = 0; i<length; i++) {
      x = numberList[i];
      dMin = initdMin;
      jK = 0;

      for(j = 0; j < k; j++) {
        d = Math.abs(x - means[j]);
        if(d < dMin) {
          dMin = d;
          jK = j;
        }
      }
      if(n == N - 1) {
        if(returnIndexes) {
          clusters[jK].push(i);
        } else {
          clusters[jK].push(x);
        }
      }

      nValuesInCluster[jK]++;

      nextMeans[jK] = ((nValuesInCluster[jK] - 1) * nextMeans[jK] + x) / nValuesInCluster[jK];
    }
  }

  return clusters;
};


/**
 * smooth a numberList by calculating averages with neighbors
 * @param  {NumberList} numberList
 *
 * @param  {Number} intensity weight for neighbors in average (0 &lt;=intensity&lt;=0.5)
 * @param  {Number} nIterations number of ieterations
 * @return {NumberList}
 * tags:statistics
 */
NumberListOperators.averageSmoother = function(numberList, intensity, nIterations) {
  if(numberList==null) return;

  nIterations = nIterations == null ? 1 : nIterations;
  intensity = intensity == null ? 0.1 : intensity;

  intensity = Math.max(Math.min(intensity, 0.5), 0);
  intensity *= 0.6; // we can get rippling if intensity is too high
  var n = numberList.length;

  var list1 = numberList.clone();
  var list2 = numberList.clone();
  var i,j;

  // Use values in L1 to construct smoothed values in L2
  // alternate lists so we don't need to copy full list back and forth
  var L1,L2,nNeighbors,val;
  for(i = 0; i < nIterations; i++) {
    L1 = i % 2 ? list2 : list1;
    L2 = i % 2 ? list1 : list2;
    for(j = 0; j < n; j++){
      val = 0;
      nNeighbors = 0;
      if(j - 1 >= 0){
        nNeighbors++;
        val += L1[j-1] * intensity;
      }
      if(j + 1 < n){
        nNeighbors++;
        val += L1[j+1] * intensity;
      }
      L2[j] = val + L1[j] * (1 - nNeighbors*intensity);
    }
  }

  return L2;
};

/**
 * finds closest valu to a given number in a list
 * @param  {NumberList} numberList
 * @param  {Number} value
 *
 * @param {Boolean} returnValue true:return value (default), false:return index
 * @param {Boolean} mode, 0:closest (default), 1:closest below or equal, 2:closest above or equal, 3:closest below, 4:closest above
 * @return {Number} value or index of closest value
 * tags:
 */
NumberListOperators.closestValue = function(numberList, value, returnValue, mode) {
  if(numberList==null) return;
  returnValue = returnValue==null?true:returnValue;

  var dMin = Math.abs(numberList[0]-value);
  var iMin = 0;
  var d;

  for(var i=1; i<numberList.length; i++){
    d = Math.abs(numberList[i]-value);
    if(d<dMin){
      if(mode==1 && numberList[i]>value || mode==2 && numberList[i]<value || mode==3 && numberList[i]>=value || mode==4 && numberList[i]<=value) continue;
      dMin=d;
      iMin = i;
    }
  }
  return returnValue?numberList[iMin]:iMin;
};

/**
 *@todo: finish
 */
NumberListOperators.filterNumberListByInterval = function(numberList, min, max, includeMin, includeMax, returnMode) {
  return null;
};

/**
 * accepted comparison operators: "<", "<=", ">", ">=", "==", "!="
 */
NumberListOperators.filterNumberListByNumber = function(numberList, value, comparisonOperator, returnIndexes) {
  returnIndexes = returnIndexes || false;
  var newNumberList = new NumberList();
  var i;

  if(returnIndexes) {
    switch(comparisonOperator) {
      case "<":
        for(i = 0; numberList[i] != null; i++) {
          if(numberList[i] < value) {
            newNumberList.push(i);
          }
        }
        break;
      case "<=":
        for(i = 0; numberList[i] != null; i++) {
          if(numberList[i] <= value) {
            newNumberList.push(i);
          }
        }
        break;
      case ">":
        for(i = 0; numberList[i] != null; i++) {
          if(numberList[i] > value) {
            newNumberList.push(i);
          }
        }
        break;
      case ">=":
        for(i = 0; numberList[i] != null; i++) {
          if(numberList[i] >= value) {
            newNumberList.push(i);
          }
        }
        break;
      case "==":
        for(i = 0; numberList[i] != null; i++) {
          if(numberList[i] == value) {
            newNumberList.push(i);
          }
        }
        break;
      case "!=":
        for(i = 0; numberList[i] != null; i++) {
          if(numberList[i] != value) {
            newNumberList.push(i);
          }
        }
        break;
    }

  } else {
    switch(comparisonOperator) {
      case "<":
        for(i = 0; numberList[i] != null; i++) {
          if(numberList[i] < value) {
            newNumberList.push(numberList[i]);
          }
        }
        break;
      case "<=":
        for(i = 0; numberList[i] != null; i++) {
          if(numberList[i] <= value) {
            newNumberList.push(numberList[i]);
          }
        }
        break;
      case ">":
        for(i = 0; numberList[i] != null; i++) {
          if(numberList[i] > value) {
            newNumberList.push(numberList[i]);
          }
        }
        break;
      case ">=":
        for(i = 0; numberList[i] != null; i++) {
          if(numberList[i] >= value) {
            newNumberList.push(numberList[i]);
          }
        }
        break;
      case "==":
        for(i = 0; numberList[i] != null; i++) {
          if(numberList[i] == value) {
            newNumberList.push(numberList[i]);
          }
        }
        break;
      case "!=":
        for(i = 0; numberList[i] != null; i++) {
          if(numberList[i] != value) {
            newNumberList.push(numberList[i]);
          }
        }
        break;
    }
  }

  return newNumberList;
};


/**
 * builds a rectangle that defines the boundaries of two numberLists interpreted as x and y coordinates
 * @param  {NumberList} numberListX
 * @param  {NumberList} numberListY
 * @return {Rectangle}
 */
NumberListOperators.frameFromTwoNumberLists = function(numberListX, numberListY){
  if(numberListX==null || numberListY==null) return;
  var intX = numberListX.getInterval();
  var intY = numberListY.getInterval();
  return new Rectangle(intX.x, intY.x, intX.getAmplitude(), intY.getAmplitude());
};

/**
 * builds a NumberList that gives histogram counts
 * @param  {NumberList} numberList
 * 
 * @param  {Number} nBins number of bins to use (default 10)
 * @param  {Interval} interval range of values (default use actual range of input numberList)
 * @param {Number} mode return mode<br>0:(default) return the number of numbers on each bin<br>1:return the bin index for each value
 * @return {NumberList}
 * tags:statistics
 */
NumberListOperators.rangeCounts = function(numberList, nBins, interval, mode){
  if(numberList==null) return;
  nBins = nBins == null ? 10 : nBins;
  
  mode = mode || 0;
  var nLCounts = mode==1?[]:ListGenerators.createListWithSameElement(nBins,0);
  interval = interval==null ? numberList.getInterval() : interval;
  if(interval == null)
    return nLCounts;
  var amp = interval.y-interval.x;
  var bin,len=numberList.length;
  var binIndexes = new NumberList();
  for(var i=0;i<len;i++){
    bin = Math.min(Math.floor(nBins*(numberList[i] - interval.x)/amp),nBins-1);
    binIndexes[i] = bin;
    if(mode===0) nLCounts[bin]++;
  }
  if(mode==1) return binIndexes;
  return nLCounts;
};

// NumberListOperators.rangeCounts = function(numberList, nBins, interval, mode){
//   if(numberList==null) return;
//   nBins = nBins == null ? 100 : nBins;
//   interval = interval==null ? numberList.getInterval() : interval;
//   var amp = interval.y - interval.x;

//   mode = mode || 0;
//   var nLCounts = ListGenerators.createListWithSameElement(nBins,0);
//   var bin,len=numberList.length;
//   var binIndexes = new NumberList();
//   for(var i=0;i<len;i++){
//     bin = Math.min(Math.floor(nBins*(numberList[i]-interval.x)/amp),nBins-1);
//     binIndexes[i] = bin;
//     nLCounts[bin]++;
//   }
//   if(mode==1) return binIndexes;
//   return nLCounts;
// };

/**
 * builds a NumberTable that gives 2D histogram counts for a pair of NumberLists
 * @param  {NumberList} nL1
 * @param  {NumberList} nL2
 * 
 * @param  {Number} nBins1 number of bins to use for nL1 (default 25)
 * @param  {Number} nBins2 number of bins to use for nL2 (default 25)
 * @param  {Interval} int1 range of values (default use actual range of input nL1)
 * @param  {Interval} int2 range of values (default use actual range of input nL2)
 * @return {NumberTable}
 * tags:statistics
 */
NumberListOperators.rangeCounts2D = function(nL1,nL2,nBins1,nBins2,int1,int2){
  if(nL1==null || nL2==null || nL1.length != nL2.length) return;
  nBins1 = nBins1 == null ? 25 : nBins1;
  nBins2 = nBins2 == null ? 25 : nBins2;
  int1 = int1==null ? nL1.getInterval() : int1;
  int2 = int2==null ? nL2.getInterval() : int2;
  var nT = TableGenerators.createTableWithSameElement(nBins1,nBins2,0);
  var f1,f2,bin1,bin2,len=nL1.length;
  for(var i=0;i<len;i++){
    f1 = int1.getInverseInterpolatedValue(nL1[i]);
    bin1 = Math.max(0,Math.min(Math.floor(f1*nBins1),nBins1-1));
    f2 = int2.getInverseInterpolatedValue(nL2[i]);
    bin2 = Math.max(0,Math.min(Math.floor(f2*nBins2),nBins2-1));
    nT[bin1][bin2]++;
  }
  return nT;
};

/**
 * in case the numberList is sorted, it generates a new one with lower values, by subtracting consecutive numbers
 * @param {NumberList} nl
 *
 * @param {Boolean} compress true (default) for compression, false for decompression
 * @return {NumberList}
 * tags:
 */
NumberListOperators.simpleCompression = function(nl, compress){
  if(nl==null) return null;

  compress = compress==null?true:compress;

  var i;
  var newNl = new NumberList();
  newNl.name = nl.name;

  if(nl.length===0) return newNl;

  newNl[0] = nl[0];

  if(compress){
    for(i=1; i<nl.length; i++){
      newNl[i] = nl[i]-nl[i-1]-1;
    }
  } else {
    for(i=1; i<nl.length; i++){
      newNl[i] = newNl[i-1]+nl[i]+1;
    }
  }
  return newNl;
};

/**
 * Get any outliers in the numberList, based on Tukey's test (+- IQR * k)
 * @param {NumberList} nl
 *
 * @param {Number} retMode <|>0: return outlier values(default)<|>1: return indicies of outliers<|>2: return NumberTable having values in column 0 and indicies in column 1<|>3: return list without outliers<|>4: numberList of same length as input, with 0s and 1s for flagging outliers
 * @param {Number} kValue IQR range multiple (default=1.5), larger value limits to more extreme outliers
 * @param {Number} limit - if specified then return at most this many outliers. The most extreme cases are returned.
 * @return {NumberList}
 * tags: statistics
 */
NumberListOperators.getOutliers = function(nl, retMode, kValue, limit){
  if(nl==null) return null;
  if(kValue==null) kValue=1.5;
  if(retMode==null) retMode=0;

  // based on Tukey's test using IQR
  var nLQ=nl.getQuantiles(4);
  var IQR = nLQ[2]-nLQ[0];
  var lower=nLQ[0]-kValue*IQR;
  var upper=nLQ[2]+kValue*IQR;
  if(IQR == 0){
    // doesn't seem to work very well. Use a heuristic based on stDev instead
    var sd = nl.getStandardDeviation();
    lower = nLQ[0] - 2*kValue*sd;
    upper = nLQ[0] + 2*kValue*sd;
  }

  var nt = new NumberTable(3); // 0 are values, 1 are indices, 2 deltas
  nt[0].name='Values';
  nt[1].name='Indicies';
  // use _min and _max set inside getQuantiles to shortcut when there are no outliers
  if(nLQ._min < lower || nLQ._max > upper)
    for(var i=0;i<nl.length;i++){
      if(nl[i] < lower){
        nt[0].push(nl[i]);
        nt[1].push(i);
        nt[2].push(lower - nl[i]);
      }
      else if(nl[i] > upper){
        nt[0].push(nl[i]);
        nt[1].push(i);
        nt[2].push(nl[i] - upper);
      }
    }
  if(limit != null && nt[0].length > limit){
    nt = nt.getListsSortedByList(nt[2],false);
    if(limit==0)
      nt = nt.getRows(new NumberList());
    else 
      nt = nt.sliceRows(0,Math.max(0,Math.ceil(limit)-1));
  }
  // remove delta column
  nt = nt.getSubList(0,1);

  if(retMode == 3) return nl.getWithoutElements(nt[0]);

  // sort by size of outliers
  nt = nt.getListsSortedByList(nt[0],false);
  if(retMode == 0)
    return nt[0];
  else if(retMode == 1)
    return nt[1];

  if(retMode == 4){
    var flags = new NumberList();
    for(i=0;i<nl.length;i++){
      flags[i]=0;
    }
    for(i=0;i<nt[1].length; i++){
      flags[nt[1][i]] = 1;;
    }
    return flags;
  }


  return nt;
};

/**
 * @ignore
 */
NumberListOperators._Regression = function(method, data, order, options) {
/**
The MIT License (MIT)

Copyright (c) Tom Alexander <me@tomalexander.co.nz>

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*
* Regression.JS - Regression functions for javascript
* http://tom-alexander.github.com/regression-js/
*
**/

/**
 * Determine the coefficient of determination (r^2) of a fit from the observations and predictions.
 *
 * @param {Array<Array<number>>} observations - Pairs of observed x-y values
 * @param {Array<Array<number>>} predictions - Pairs of observed predicted x-y values
 *
 * @return {number} - The r^2 value, or NaN if one cannot be calculated.
 */

function determinationCoefficient(observations, predictions) {
  var sum = observations.reduce(function (accum, observation) { return accum + observation[1]; }, 0);
  var mean = sum / observations.length;

  // Sum of squares of differences from the mean in the dependent variable
  var ssyy = observations.reduce(function (accum, observation) {
    var diff = observation[1] - mean;
    return accum + diff * diff;
  }, 0);

  // Sum of squares of resudulals
  var sse = observations.reduce(function (accum, observation, ix) {
    var prediction = predictions[ix];
    var resid = observation[1] - prediction[1];
    return accum + resid * resid;
  }, 0);

  // If ssyy is zero, r^2 is meaningless, so NaN is an appropriate answer.
  return 1 - (sse / ssyy);
}

/**
 * Determine the solution of a system of linear equations A * x = b using Gaussian elimination.
 *
 * @param {Array<Array<number>>} matrix - A 2-d matrix of data in row-major form [ A | b ]
 * @param {number} order - How many degrees to solve for
 *
 * @return {Array<number>} - Vector of normalized solution coefficients matrix (x)
 */
function gaussianElimination(matrix, order) {
  var i = 0;
  var j = 0;
  var k = 0;
  var maxrow = 0;
  var tmp = 0;
  var n = matrix.length - 1;
  var coefficients = new Array(order);

  for (i = 0; i < n; i++) {
    maxrow = i;
    for (j = i + 1; j < n; j++) {
      if (Math.abs(matrix[i][j]) > Math.abs(matrix[i][maxrow])) {
        maxrow = j;
      }
    }

    for (k = i; k < n + 1; k++) {
      tmp = matrix[k][i];
      matrix[k][i] = matrix[k][maxrow];
      matrix[k][maxrow] = tmp;
    }

    for (j = i + 1; j < n; j++) {
      for (k = n; k >= i; k--) {
        matrix[k][j] -= matrix[k][i] * matrix[i][j] / matrix[i][i];
      }
    }
  }

  for (j = n - 1; j >= 0; j--) {
    tmp = 0;
    for (k = j + 1; k < n; k++) {
      tmp += matrix[k][j] * coefficients[k];
    }

    coefficients[j] = (matrix[n][j] - tmp) / matrix[j][j];
  }

  return coefficients;
}

/** Precision to use when displaying string form of equation */
var _DEFAULT_PRECISION = 2;

/**
 * Round a number to a precision, specificed in number of decimal places
 *
 * @param {number} number - The number to round
 * @param {number} precision - The number of decimal places to round to:
 *                             > 0 means decimals, < 0 means powers of 10
 *
 *
 * @return {numbr} - The number, rounded
 */
function _round(number, precision) {
  var factor = Math.pow(10, precision);
  return Math.round(number * factor) / factor;
}

/**
 * The set of all fitting methods
 *
 * @namespace
 */
var methods = {
  linear: function (data, _order, options) {
    var sum = [0, 0, 0, 0, 0];
    var results;
    var gradient;
    var intercept;
    var len = data.length;

    for (var n = 0; n < len; n++) {
      if (data[n][1] !== null) {
        sum[0] += data[n][0];
        sum[1] += data[n][1];
        sum[2] += data[n][0] * data[n][0];
        sum[3] += data[n][0] * data[n][1];
        sum[4] += data[n][1] * data[n][1];
      }
    }

    gradient = (len * sum[3] - sum[0] * sum[1]) / (len  * sum[2] - sum[0] * sum[0]);
    intercept = (sum[1] / len) - (gradient * sum[0]) / len;

    results = data.map(function (xyPair) {
      var x = xyPair[0];
      return [x, gradient * x + intercept];
    });

    return {
      r2: determinationCoefficient(data, results),
      equation: [gradient, intercept],
      points: results,
      string: 'y = ' + _round(gradient, options.precision) + 'x + ' + _round(intercept, options.precision),
      func: new Function('x','return ' + _round(gradient, options.precision) + '*x + ' + _round(intercept, options.precision) + ';')
    };
  },

  linearthroughorigin: function (data, _order, options) {
    var sum = [0, 0];
    var gradient;
    var results;

    for (var n = 0; n < data.length; n++) {
      if (data[n][1] !== null) {
        sum[0] += data[n][0] * data[n][0]; // sumSqX
        sum[1] += data[n][0] * data[n][1]; // sumXY
      }
    }

    gradient = sum[1] / sum[0];

    results = data.map(function (xyPair) {
      var x = xyPair[0];
      return [x, gradient * x];
    });

    return {
      r2: determinationCoefficient(data, results),
      equation: [gradient],
      points: results,
      string: 'y = ' + _round(gradient, options.precision) + 'x',
      func: new Function('x','return ' + _round(gradient, options.precision) + '*x;')
    };
  },

  exponential: function (data, _order, options) {
    var sum = [0, 0, 0, 0, 0, 0];
    var denominator;
    var coeffA;
    var coeffB;
    var results;

    for (var n = 0; n < data.length; n++) {
      if (data[n][1] !== null) {
        sum[0] += data[n][0];
        sum[1] += data[n][1];
        sum[2] += data[n][0] * data[n][0] * data[n][1];
        if(data[n][1] != 0){
          // when y==0 log is undefined but result gets zeroed out anyways
          // function will still fail with negative y vals
          sum[3] += data[n][1] * Math.log(data[n][1]);
          sum[4] += data[n][0] * data[n][1] * Math.log(data[n][1]);
        }
        sum[5] += data[n][0] * data[n][1];
      }
    }

    denominator = (sum[1] * sum[2] - sum[5] * sum[5]);
    coeffA = Math.exp((sum[2] * sum[3] - sum[5] * sum[4]) / denominator);
    coeffB = (sum[1] * sum[4] - sum[5] * sum[3]) / denominator;

    results = data.map(function (xyPair) {
      var x = xyPair[0];
      return [x, coeffA * Math.exp(coeffB * x)];
    });

    return {
      r2: determinationCoefficient(data, results),
      equation: [coeffA, coeffB],
      points: results,
      string: 'y = ' + _round(coeffA, options.precision) + 'e^(' + _round(coeffB, options.precision + 2) + 'x)',
      func: new Function('x','return ' + _round(coeffA, options.precision) + ' * Math.exp(' + _round(coeffB, options.precision + 2) + ' * x);')
    };
  },

  logarithmic: function (data, _order, options) {
    var sum = [0, 0, 0, 0];
    var coeffA;
    var coeffB;
    var results;
    var len = data.length;

    for (var n = 0; n < len; n++) {
      if (data[n][1] !== null) {
        sum[0] += Math.log(data[n][0]);
        sum[1] += data[n][1] * Math.log(data[n][0]);
        sum[2] += data[n][1];
        sum[3] += Math.pow(Math.log(data[n][0]), 2);
      }
    }

    coeffB = (len * sum[1] - sum[2] * sum[0]) / (len * sum[3] - sum[0] * sum[0]);
    coeffA = (sum[2] - coeffB * sum[0]) / len;

    results = data.map(function (xyPair) {
      var x = xyPair[0];
      return [x, coeffA + coeffB * Math.log(x)];
    });

    return {
      r2: determinationCoefficient(data, results),
      equation: [coeffA, coeffB],
      points: results,
      string: 'y = ' + _round(coeffA, options.precision) + ' + ' + _round(coeffB, options.precision) + ' ln(x)',
      func: new Function('x','return ' + _round(coeffA, options.precision) + ' + ' + _round(coeffB, options.precision) + '* Math.log(x);')
    };
  },

  power: function (data, _order, options) {
    var sum = [0, 0, 0, 0];
    var coeffA;
    var coeffB;
    var results;
    var len = data.length;

    for (var n = 0; n < len; n++) {
      if (data[n][1] !== null) {
        sum[0] += Math.log(data[n][0]);
        sum[1] += Math.log(data[n][1]) * Math.log(data[n][0]);
        sum[2] += Math.log(data[n][1]);
        sum[3] += Math.pow(Math.log(data[n][0]), 2);
      }
    }

    coeffB = (len * sum[1] - sum[2] * sum[0]) / (len * sum[3] - sum[0] * sum[0]);
    coeffA = Math.exp((sum[2] - coeffB * sum[0]) / len);

    results = data.map(function (xyPair) {
      var x = xyPair[0];
      return [x, coeffA * Math.pow(x, coeffB)];
    });

    return {
      r2: determinationCoefficient(data, results),
      equation: [coeffA, coeffB],
      points: results,
      string: 'y = ' + _round(coeffA, options.precision) + 'x^' + _round(coeffB, options.precision + 2),
      func: new Function('x','return ' + _round(coeffA, options.precision) + ' * Math.pow(x,' + _round(coeffB, options.precision + 2) + ');')
    };
  },

  polynomial: function (data, order, options) {
    var lhs = [];
    var rhs = [];
    var a = 0;
    var b = 0;
    var c;
    var k;

    var i;
    var j;
    var l;
    var len = data.length;

    var results;
    var equation;
    var string;
    var sFunc;

    if (typeof order === 'undefined') {
      k = 3;
    } else {
      k = order + 1;
    }

    for (i = 0; i < k; i++) {
      for (l = 0; l < len; l++) {
        if (data[l][1] !== null) {
          a += Math.pow(data[l][0], i) * data[l][1];
        }
      }

      lhs.push(a);
      a = 0;

      c = [];
      for (j = 0; j < k; j++) {
        for (l = 0; l < len; l++) {
          if (data[l][1] !== null) {
            b += Math.pow(data[l][0], i + j);
          }
        }
        c.push(b);
        b = 0;
      }
      rhs.push(c);
    }
    rhs.push(lhs);

    equation = gaussianElimination(rhs, k);

    results = data.map(function (xyPair) {
      var x = xyPair[0];

      var answer = equation.reduce(function (sum, coeff, power) {
        return sum + coeff * Math.pow(x, power);
      }, 0);

      return [x, answer];
    });

    string = 'y = ';
    sFunc = 'return ';
    for (i = equation.length - 1; i >= 0; i--) {
      if (i > 1) {
        string += _round(equation[i], options.precision+2)  + 'x^' + i + ' + ';
        sFunc  += _round(equation[i], options.precision+2)  + ' * Math.pow(x,' + i + ') + ';
      } else if (i === 1) {
        string += _round(equation[i], options.precision) + 'x' + ' + ';
        sFunc  += _round(equation[i], options.precision) + '* x' + ' + ';
      } else {
        string += _round(equation[i], options.precision);
        sFunc  += _round(equation[i], options.precision);
      }
    }
    sFunc += ';';

    return {
      r2: determinationCoefficient(data, results),
      equation: equation,
      points: results,
      string: string,
      func: new Function('x',sFunc)
    };
  },

  lastvalue: function (data, _order, options) {
    var results = [];
    var lastvalue = null;

    for (var i = 0; i < data.length; i++) {
      if (data[i][1] !== null && isFinite(data[i][1])) {
        lastvalue = data[i][1];
        results.push([data[i][0], data[i][1]]);
      } else {
        results.push([data[i][0], lastvalue]);
      }
    }

    return {
      r2: determinationCoefficient(data, results),
      equation: [lastvalue],
      points: results,
      string: '' + _round(lastvalue, options.precision),
    };
  },
};

function invoke(method, data, order, options) {
  var methodOptions = (
    ((typeof order === 'object') && (typeof options === 'undefined'))
      ? order
      : options || {}
  );

  if (!methodOptions.precision) {
    methodOptions.precision = _DEFAULT_PRECISION;
  }

  if (typeof method === 'string') {
    return methods[method.toLowerCase()](data, order, methodOptions);
  }
  return null;
}

// this is the actual code for _Regression
return invoke(method, data, order, options);

};

/**
 * A general regression function between two numberLists that supports linear, exponential, logarithmic, power, and polynomial regressions
 * @param  {NumberList} numberListY is the list of values, null values will be filled with forecasted values
 *
 * @param  {NumberList} numberListX of the same length as numberListY, if not specified the sequence 1,2,3,... is used
 * @param  {String} method type of regression to use: <|>linear: linear regression (default)<|>exponential: exponential regression<|>logarithmic: logarithmic regression<|>power: power law regression<|>polynomial: polynomial regression, specify order of polynomial in next inlet
 * @param  {Number} order for polynomial regression, default=2
 * @return {NumberList} YResults including input and any forecasted values
 * @return {NumberList} Coefficients of the regression equation
 * @return {String} Equation in string form
 * @return {Number} R^2 value
 * tags:statistics
 */
NumberListOperators.regression = function(numberListY, numberListX, method, order) {
  if(numberListY == null || numberListY.length == 0) return null;
  if(numberListX == null)
    numberListX = NumberListGenerators.createSortedNumberList(numberListY.length,1); 
  method = method == null ? 'linear' : method;
  if(typeof method != 'string' || 
    (method != 'linear' && method != 'exponential' && method != 'logarithmic' && method != 'power' && method != 'polynomial') )
    throw new Error('Invalid method in NumberListOperators.regression: ' + method);
  order = order == null ? 2 : order;
  
  // make paired array using data
  var data = [];
  for(var i=0;i<numberListX.length;i++){
    data.push([numberListX[i],numberListY[i] == null ? null : numberListY[i]]);
  }
  var result = NumberListOperators._Regression(method, data, order, {precision:3});

  var nLY = new NumberList(); nLY.name = method + ' Regression Series';
  for(i=0; i<result.points.length; i++){
    nLY.push(result.points[i][1]);
  }

  var nLCoeff = NumberList.fromArray(result.equation);
  nLCoeff.name = 'Coefficients';

  return [
    {
      type: "NumberList",
      name: "Y Results",
      description: "Y Results are the forecasted values",
      value: nLY
    },
    {
      type: "NumberList",
      name: "Coefficients",
      description: "Coefficients of the regression equation",
      value: nLCoeff
    },
    {
      type: "String",
      name: "Equation",
      description: "Equation in string form",
      value: result.string
    },
    {
      type: "Number",
      name: "R^2",
      description: "R^2 value",
      value: result.r2
    },
    {
      type: "Function",
      name: "fnModel",
      description: "Function that takes input and calculates output based on model",
      value: result.func
    }
  ];
};

/**
 * Get the minimum and maximum number of decimals in numbers of the list
 * @param {NumberList} nl input numberlist
 *
 * @return {Interval} int with int.x having minimum number of decimals and int.y the maximum
 * tags:statistics
 */
NumberListOperators.getDecimalsInterval = function(nl){
  if(nl == null) return null;
  var intDecimals = new Interval(0,0);
  var i,d;
  for(i = 0;i < nl.length; i++){
    d = NumberOperators.decimalPlaces(nl[i]);
    intDecimals.adjustToContain(d);
  }
  return intDecimals;
};

/**
 * Set the number of decimals in numbers of the list
 * @param {NumberList|Table} nl input numberlist. Can also be a Table in which case every NumberList will be changed
 * @param {Number} nDecimals to include
 *
 * @return {NumberList} the modified numberlist
 * tags: statistics
 */
NumberListOperators.setDecimals = function(nl,nDecimals){
  if(nl == null) return null;
  var i,d;
  if(nl.isTable){
    var nT = instantiateWithSameType(nl);
    nT.name = nl.name;
    for(i = 0;i < nl.length; i++){
      if(nl[i].type == 'NumberList')
        nT.push(NumberListOperators.setDecimals(nl[i],nDecimals));
      else
        nT.push(nl[i].clone());
    }
    return nT;
  }
  var newList = new NumberList();
  newList.name = nl.name;
  for(i = 0;i < nl.length; i++){
    d = nl[i] == null ? null : Number(nl[i].toFixed(nDecimals));
    newList.push(d);
  }
  return newList;
};

/**
 * Get the minimum and maximum number of significant digits in numbers of the list
 * @param {NumberList} nl input numberlist
 *
 * @return {Interval} int with int.x having minimum number of signifcant digits and int.y the maximum
 * tags: statistics
 */
NumberListOperators.getSignificantDigitsInterval = function(nl){
  if(nl == null) return null;
  var intDigits = new Interval(0,0);
  var i,d;
  for(i = 0;i < nl.length; i++){
    d = NumberOperators.significantDigits(nl[i]);
    intDigits.adjustToContain(d);
  }
  return intDigits;
};

/**
 * Set the number of significant digits in numbers of the list
 * @param {NumberList} nl input numberlist
 * @param {Number} nDigits of significance
 *
 * @return {NumberList} the modified numberlist
 * tags: statistics
 */
NumberListOperators.setSignificantDigits = function(nl,nDigits){
  if(nl == null) return null;
  var i,d;
  if(nl.type == 'NumberTable'){
    var nT = new NumberTable();
    nT.name = nl.name;
    for(i = 0;i < nl.length; i++){
      nT.push(NumberListOperators.setSignificantDigits(nl[i],nDigits));
    }
    return nT;
  }
  var newList = new NumberList();
  newList.name = nl.name;
  for(i = 0;i < nl.length; i++){
    // leave null, spaces, other non-numerics as they were
    if(nl[i] == null || isNaN(nl[i])){
      newList.push(nl[i]);
      continue;
    }
    d = Number((nl[i]).toPrecision(nDigits))
    newList.push(d);
  }
  return newList;
};

/**
 * Set decimals of numbers in the list to the fewest possible while retaining ability to discriminate unequal values
 * @param {NumberList} nl input numberlist
 *
 * @return {NumberList} the modified numberlist
 * tags: statistics
 */
NumberListOperators.setMinimumDiscriminatingPrecision = function(nL){
  if(nL == null) return null;
  var intDecimals = NumberListOperators.getDecimalsInterval(nL);
  // if integers or only 1 decimal max then return as is
  if(intDecimals.y <= 1) return nL;
  var nUnique = nL.getWithoutRepetitions().length;
  if(nUnique == 1)
    return NumberListOperators.setDecimals(nL,Math.min(4,intDecimals.y));
  var i,nLTest;
  for(i=2; i <= intDecimals.y; i++){
    nLTest = NumberListOperators.setDecimals(nL,i);
    if(nLTest.getWithoutRepetitions().length == nUnique)
      return nLTest;
  }
  return nLTest;
};

/**
 * calculates logistic function values for numbers in the list
 * @param {NumberList} nl is the input to transform
 *
 * @param {Number} maxValue maximum resulting value (default:1)
 * @param {Number} midpoint x-value of the curves midpoint (default:0)
 * @param {Number} k is the steepness of the curve (default:1)
 * @return {NumberList} result
 * tags: statistics
 */
NumberListOperators.transformByLogistic = function(nl,maxValue,midpoint,k) {
  if(nl==null) return null;
  maxValue = maxValue == null ? 1 : maxValue;
  midpoint = midpoint == null ? 0 : midpoint;
  k = k == null ? 1 : k;
  
  var i;
  var newList = new NumberList();
  newList.name = nl.name;
  for(i = 0;i < nl.length; i++){
    // leave null, spaces, other non-numerics as they were
    if(nl[i] == null || isNaN(nl[i])){
      newList.push(nl[i]);
      continue;
    }
    newList.push(NumberOperators.logistic(nl[i],maxValue,midpoint,k));
  }
  return newList;
};

/**
 * returns a list of values which shows the distribution of the input list
 * @param {NumberList} nL is the input to analyze
 *
 * @param  {Number} mode <|>0: regular (default)<|>1: random
 * @param  {Number} bins number of bins (default:50)
 * @return {NumberList} result is a numberList of values where the value reflects the local density of points
 * tags: statistics
 */
NumberListOperators.getDistributionHeights = function(nL,mode,nBins) {
    if(nL == null) return null;
    mode = (mode == null || mode != 1) ? 0 : mode;
    nBins = nBins == null ? 50 : nBins;
    var i,y,count;
    var nLBinCounts = NumberListOperators.rangeCounts(nL,nBins,null,0);
    var nLBin = NumberListOperators.rangeCounts(nL,nBins,null,1);
    var nLY = new NumberList();
    NumberOperators.randomSeed(1);
    for(i=0;i<nL.length;i++){
        count = nLBinCounts[nLBin[i]];
        if(mode == 0){
          y = count - 1; //want it to start at bottom when plotted
          nLBinCounts[nLBin[i]]--;
        }
        else
          y = NumberOperators.random()*count;
        nLY.push(y);
    }
    NumberOperators.randomSeedPop();
    return nLY;
};

/**
 * Find the nearest k neighbours to a numberList in terms of euclidean distance. Items in rows and features in columns.
 * @param  {NumberTable} nT table of numberLists to search
 * @param  {Number|NumberList} nL numberList to find nearest neighbours for. Can also be a row index of the input numberTable.
 *
 * @param  {Number} k the number of nearest neighbours to find (default:1)
 * @param  {Number} mode <|>0: return the nearest neighbours(default)<|>1: return the row indexes<|>2: return table with row indexes and distances
 * @param  {Boolean} bItemsInColumns - if true the items are in columns (default:false)
 * @param  {String} distanceMetric is the type of distance metric to use. Values:<|>euclidean: distance (default)<|>cosine: distance<|>manhattan: distance
 * @param  {Boolean} bFindFarthest - if true then find the farthest neighbours instead of the nearest (default:false)
 * @return {NumberTable} the result
 * tags: statistics, machine-learning
 */
NumberListOperators.kNearestNeighbours = function(nT, nL, k, mode, bItemsInColumns, distanceMetric, bFindFarthest) {
  //very inefficient, see this video for better solution: https://www.youtube.com/watch?v=eaYX0Ee0Kcg&list=RDCMUCxX9wt5FWQUAAz4UrysqK9A&index=27
  if(nT == null || nL == null || nT.length == 0) return;
  k = k == null ? 1 : k;
  mode = mode == null ? 0 : mode;
  bItemsInColumns = bItemsInColumns == null ? false : bItemsInColumns;
  bFindFarthest = bFindFarthest == null ? false : bFindFarthest;
  var bInsideInput = typeof nL == 'number';
  var iRowInside;

  if(!bItemsInColumns)
    nT = nT.getTransposed();
  if(bInsideInput){
    if(nL < 0 || nL >= nT.length || Math.floor(nL) != nL)
      throw new Error('Invalid value for second inlet.');
    iRowInside = nL;
    nL = nT[iRowInside];
  }
  if(nL.length != nT[0].length)
    throw new Error('The input numberList must have the same number of values as columns of the numberTable.');
  var nTDistances = new NumberTable();
  nTDistances.push(new NumberList());
  nTDistances.push(new NumberList());
  nTDistances.assignNames('index,distance');
  for(var i=0;i < nT.length; i++){
    if(bInsideInput && i == iRowInside) continue;
    nTDistances[0].push(i);
    nTDistances[1].push(NumberListOperators.distance(nT[i],nL,distanceMetric));
  }
  nTDistances = nTDistances.getListsSortedByList(1,!bFindFarthest,true);
  nTDistances = nTDistances.sliceRows(0,k-1);
  if(mode == 0)
    return nT.getColumns(nTDistances[0]);
  else if(mode == 1)
    return nTDistances[0];
  return nTDistances;
};

/**
 * Measures how skewed a numeric series is, effectively detecting whether is an exponential distribution
 * @param  {NumberList} list to transform
 * 
 * @param  {Number} mode <|>0:sample skewness (default)<|>1:G1 sample skewness<|>2:median/mean ratio (log2 applied, exponential distributions above ~0.5)
 * @return {Number} skewness metric in [-1, ∞)
 * tags:statistics
 */
NumberListOperators.skewness = function(nL, mode){
  if(nL==null) return;
  var mean = nL.getAverage();
  mode = mode==null?0:mode;

  if(mode>=2){
    return Math.log2(mean/nL.getMedian());
  }

  var sd = nL.getStandardDeviation();
  if(sd==0) return 0;

  switch(mode){
    case 0:
      var s = 0;
      for(var i=0;i<nL.length;i++){
          s += Math.pow(nL[i]-mean,3);
      }
      return s/nL.length/Math.pow(sd,3);
    case 1:
      if(sd==0) return 0;
      var s = 0;
      for(var i=0;i<nL.length;i++){
          s += Math.pow(nL[i]-mean,3);
      }
      var b1 = s/nL.length/Math.pow(sd,3);
      var n=nL.length;
      return n*n/((n-1)*(n-2))*b1;
  }
};

/**
 * Produce a new (same size) list with changes that remove information, useful for anonimization, syhntesis… for similar categorical transformations use erodeList
 * @param  {List} list to transform
 * @param  {Number} mode <br>0:add random noise<br>1:random numbers preserving sorting
 * @return {NumberList} output string
 */
NumberListOperators.erodeNumberList = function(nl, mode){

};

/**
 * Calculate the gini coefficient for the list of numbers, if negative values are present then all values are shifted so they are positive
 * @param  {NumberList|NumberTable} list to calculate gini value. Can also be a NumberTable in which case a list of values are returned.
 *
 * @param  {boolean} bApplySmallSampleCorrection if true then multiply by n/(n-1) (default)
 * @param  {number} nSlots the total number of values to use. The default is to use the length of the input list. Extra slots will be filled with zero
 * @return {number} the gini value
 * tags:statistics
 * examples:jeff/examples/giniCoefficient
*/
NumberListOperators.giniCoefficient = function(nL, bApplySmallSampleCorrection, nSlots){
  if(nL == null) return null;
  bApplySmallSampleCorrection = bApplySmallSampleCorrection == null ? true : bApplySmallSampleCorrection;

  if(nL.type == 'NumberTable'){
    var nLRes = new NumberList();
    nLRes.name = 'Gini';
    for(let i=0; i < nL.length; i++){
      nLRes.push(NumberListOperators.giniCoefficient(nL[i],bApplySmallSampleCorrection,nSlots));
    }
    return nLRes;
  }
  if(nSlots){
    if(nSlots < nL.length)
      throw new Error('Invalid value for number of slots. It must be at least the size of the input list');
    else
      nL = nL.concat(ListGenerators.createListWithSameElement(nSlots-nL.length,0));
  }
  if(nL.length < 2) return null;
  // based on https://www.statsdirect.com/help/default.htm#nonparametric_methods/gini.htm
  var epsilon = 0.0000001;

  // make sure values are positive and > zero by at least a little
  var min = nL.getMin();
  if(min < 0)
    nL = nL.subtract(min - epsilon);
  else if(min == 0)
    nL = nL.add(epsilon);
  nL = nL.getSorted();
  var sum=0,sumTop=0,n=nL.length;
  for(let i=1; i <= n; i++){
    sum += nL[i-1];
    sumTop += (2*i - n - 1) * nL[i-1];
  }
  var G = sumTop / sum / n;
  if(bApplySmallSampleCorrection)
    G *= n/(n-1);
  return Number(G.toFixed(3));
};
